import { useState, ReactNode, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Menu, X, Home, Settings, User, LogOut,
  Moon, Sun, Monitor, Bell, ChevronDown
} from 'lucide-react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { SessionTimer } from '@/components/auth/SessionTimer'
const SithabileLogo = '/sithabile-fingerprint-logo.png'

interface DashboardLayoutProps {
  children: ReactNode
  showCustomerLogo?: boolean
  headerSlot?: ReactNode  // e.g. the dashboard's customer picker
}

export function DashboardLayout({ children, showCustomerLogo = true, headerSlot }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [showLogoutDialog, setShowLogoutDialog] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [customerLogo, setCustomerLogo] = useState<{ data_uri: string; mime_type: string; width: number; height: number } | null>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const { user, logout } = useAuth()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()

  // Main navigation items (removed Profile)
  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home, roles: ['viewer', 'admin', 'super_admin'] },
    { name: 'Settings', href: '/settings', icon: Settings, roles: ['viewer', 'admin', 'super_admin'] },
  ]

  const filteredNavigation = navigation.filter((item) => {
    if (!user) return false
    const userRole = user.role || 'viewer'
    return item.roles.includes(userRole)
  })

  const themeOptions = [
    { value: 'light' as const, icon: Sun, label: 'Light' },
    { value: 'dark' as const, icon: Moon, label: 'Dark' },
    { value: 'system' as const, icon: Monitor, label: 'System' },
  ]

  const handleLogout = async () => {
    // Debug: Log user info
    console.log('Logout clicked. User data:', {
      email: user?.email,
      is_oauth_user: user?.is_oauth_user,
      oauth_provider: user?.oauth_provider
    })
    
    // Show dialog for SSO users, direct logout for regular users
    if (user?.is_oauth_user) {
      console.log('User is OAuth user, showing dialog')
      setShowLogoutDialog(true)
    } else {
      console.log('User is not OAuth user, logging out directly')
      await logout()
      navigate('/login')
    }
  }

  const handleLogoutConfirm = async (logoutFromProvider: boolean) => {
    setShowLogoutDialog(false)
    await logout(logoutFromProvider)
    if (!logoutFromProvider) {
      navigate('/login')
    }
  }

  // Handle clicking outside user menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showUserMenu])

  // Fetch customer logo
  useEffect(() => {
    fetchCustomerLogo()

    // Listen for logo updates
    const handleLogoUpdate = (event: CustomEvent) => {
      setCustomerLogo(event.detail)
    }

    const handleLogoDelete = () => {
      setCustomerLogo(null)
    }

    window.addEventListener('logoUpdated' as any, handleLogoUpdate)
    window.addEventListener('logoDeleted' as any, handleLogoDelete)

    return () => {
      window.removeEventListener('logoUpdated' as any, handleLogoUpdate)
      window.removeEventListener('logoDeleted' as any, handleLogoDelete)
    }
  }, [])

  const fetchCustomerLogo = async () => {
    try {
      const response = await fetch('/api/v1/settings/logo')
      if (response.ok) {
        const data = await response.json()
        if (data.logo) {
          setCustomerLogo(data.logo)
        }
      }
    } catch (error) {
      console.error('Failed to fetch logo:', error)
    }
  }

  // Header centre: the page's slot (customer picker), else the customer logo, else the placeholder search.
  const headerCentre = headerSlot ?? (showCustomerLogo && customerLogo ? (
    <div className="flex items-center">
      <img
        src={customerLogo.data_uri}
        alt="Company Logo"
        className="max-h-12 max-w-[250px] object-contain"
        style={{ filter: theme === 'dark' ? 'brightness(1.2)' : undefined }}
      />
    </div>
  ) : (
    <input
      type="search"
      placeholder="Search..."
      className="w-64 px-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-gray-50 dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5869ff]"
    />
  ))

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#000742]">
      {/* Mobile sidebar backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-white dark:bg-[#000742] border-r border-gray-200 dark:border-[#5869ff]/20 transition-all duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${sidebarCollapsed ? 'w-20' : 'w-64'}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-[#5869ff]/20">
            <div className="flex items-center gap-3">
              {!sidebarCollapsed ? (
                <img src={SithabileLogo} alt="Sithabile" className="h-10" />
              ) : (
                <img src={SithabileLogo} alt="Sithabile" className="w-10 h-10 rounded-lg object-contain" />
              )}
            </div>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:block p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] text-gray-600 dark:text-gray-400"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#5869ff]/10 dark:bg-[#5869ff]/20 text-[#5869ff] dark:text-[#5869ff]'
                      : 'hover:bg-gray-100 dark:hover:bg-[#1a1f5c] text-gray-700 dark:text-gray-400'
                  }`
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.name}</span>}
              </NavLink>
            ))}
          </nav>

          {/* Version Display */}
          <div className="p-4 border-t border-gray-200 dark:border-[#5869ff]/20">
            {!sidebarCollapsed ? (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <div className="font-medium mb-1">Version {__APP_VERSION__}</div>
                <div className="text-[10px] opacity-70">
                  {__GIT_BRANCH__} • {__GIT_COMMIT__}
                </div>
              </div>
            ) : (
              <div className="text-[10px] text-center text-gray-500 dark:text-gray-400">
                v{__APP_VERSION__}
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 min-h-16 lg:h-16 bg-white/90 dark:bg-[#000742]/90 backdrop-blur-md border-b border-gray-200 dark:border-[#5869ff]/20">
          <div className="flex flex-wrap lg:flex-nowrap items-center justify-between gap-y-1 min-h-16 lg:h-full px-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] text-gray-700 dark:text-gray-300"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Centre: a slot wraps onto its own full-width row below lg (single instance in the DOM) and may shrink at lg */}
            <div className={headerSlot ? 'order-last basis-full min-w-0 pb-3 lg:order-none lg:basis-auto lg:flex-1 lg:min-w-0 lg:max-w-sm lg:px-6 lg:pb-0' : 'hidden lg:block'}>
              {headerCentre}
            </div>

            {/* Right section */}
            <div className="flex items-center gap-4">
              {/* Theme selector */}
              <div className="flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-[#1a1f5c]/50">
                {themeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setTheme(option.value)}
                    className={`p-1.5 rounded-md transition-colors ${
                      theme === option.value
                        ? 'bg-[#5869ff] text-white'
                        : 'hover:bg-gray-200 dark:hover:bg-[#1a1f5c] text-gray-600 dark:text-gray-400'
                    }`}
                    title={option.label}
                  >
                    <option.icon className="w-4 h-4" />
                  </button>
                ))}
              </div>

              {/* Session indicator */}
              <div className="hidden sm:flex items-center px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-[#1a1f5c]/50">
                <SessionTimer />
              </div>

              {/* Notifications */}
              <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] relative text-gray-600 dark:text-gray-400">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </button>

              {/* User menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-3 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c]/50 transition-colors"
                >
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {user?.full_name || 'Admin User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {user?.role?.replace('_', ' ') || 'viewer'}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5869ff] to-[#000742] flex items-center justify-center text-white font-medium">
                    {user?.full_name ? (
                      <>
                        {user.full_name.split(' ')[0]?.[0]}
                        {user.full_name.split(' ')[1]?.[0]}
                      </>
                    ) : (
                      'AU'
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown menu */}
                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#000742] rounded-lg shadow-lg border border-gray-200 dark:border-[#5869ff]/20 py-2 z-50"
                    >
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-[#5869ff]/20">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {user?.full_name || 'Admin User'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {user?.email || 'admin@netapp.com'}
                        </p>
                      </div>

                      <button
                        onClick={() => {
                          setShowUserMenu(false)
                          navigate('/profile')
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1a1f5c]/50 text-gray-700 dark:text-gray-300 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        <span>My Profile</span>
                      </button>

                      <button
                        onClick={() => {
                          setShowUserMenu(false)
                          handleLogout()
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1a1f5c]/50 text-gray-700 dark:text-gray-300 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>

      {/* SSO Logout Confirmation Dialog */}
      {showLogoutDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-[#000742] rounded-xl shadow-2xl p-6 max-w-md w-full border border-gray-200 dark:border-[#5869ff]/20">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Sign Out Options
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                You're signed in with {user?.oauth_provider === 'microsoft' ? 'Microsoft Entra ID' : 'SSO'}. 
                Would you also like to sign out from your {user?.oauth_provider === 'microsoft' ? 'Microsoft' : 'SSO provider'} account?
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => handleLogoutConfirm(true)}
                className="w-full px-4 py-2.5 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg hover:shadow-lg transition-all duration-200 font-medium transform hover:scale-[1.02]"
              >
                Sign out from both
              </button>
              
              <button
                onClick={() => handleLogoutConfirm(false)}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-[#5869ff]/30 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/50 transition-all duration-200 font-medium"
              >
                Sign out from NetApp Dashboard only
              </button>
              
              <button
                onClick={() => setShowLogoutDialog(false)}
                className="w-full px-4 py-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>

            <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Security Tip:</strong> Sign out from both for maximum security, especially on shared devices.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}